import { Link } from "react-router-dom";
import { Linkedin, Instagram, Mail, Phone, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { seoLinks } from "@/data/seoLinks";
import {
    LINKEDIN_URL,
    INSTAGRAM_URL,
    WHATSAPP_LINK,
    CONTACT_EMAIL,
} from "@/lib/config";

export default function Footer() {
    const { t } = useLang();
    return (
        <footer
            id="contact"
            data-testid="footer"
            className="relative z-10 mt-32 border-t border-white/5 bg-ink-900/80"
        >
            <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:grid-cols-12 lg:px-10">
                <div className="md:col-span-3">
                    <h3 className="font-display text-3xl font-semibold leading-tight">
                        Abhijit <span className="gold-text">JK</span>
                    </h3>
                    <p className="mt-3 text-sm text-white/60">
                        Brand & Business Growth Consultant
                    </p>
                    <p className="text-sm text-white/40">Founder of Ragi Media</p>
                    <p className="mt-8 max-w-sm text-sm text-white/50">
                        {t("footer_intro")}
                    </p>
                    <Link
                        to="/consultation"
                        data-testid="footer-book-cta"
                        className="gold-button mt-8 inline-flex items-center"
                    >
                        {t("btn_analyse")}
                    </Link>
                </div>

                <div className="md:col-span-2">
                    <p className="label-eyebrow mb-5">{t("footer_explore")}</p>
                    <ul className="space-y-3 text-sm">
                        {[
                            ["/", "Home"],
                            ["/about", "Who I Am"],
                            ["/consultation", "Consultation"],
                            ["/workshops", "Workshops"],
                            ["/downloads", "Downloads"],
                            ["/shop", "Shop"],
                            ["/case-studies", "Case Study"],
                            ["/blog", "Blog"],
                            ["/contact", "Contact"],
                        ].map(([to, label]) => (
                            <li key={to}>
                                <Link
                                    to={to}
                                    data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="text-white/70 transition hover:text-gold-300"
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <p className="label-eyebrow mb-5">Services</p>
                    <ul className="space-y-3 text-sm">
                        {[
                            ["/brand-consultant-bangalore", "Brand Consultant Bangalore"],
                            ["/business-growth-consultant-bangalore", "Business Growth Consultant"],
                            ["/brand-analysis", "Brand Analysis"],
                            ["/consultation", "Business Consultation"],
                            ["/workshops", "Business Growth Workshops"],
                            ["/website-audit", "Website Audit"],
                            ["/social-media-strategy", "Social Media Strategy"],
                            ["/logo-design-consultant", "Logo Design Consultant"],
                            ["/about", "About Abhijit JK"],
                        ].map(([to, label]) => (
                            <li key={label}>
                                <Link
                                    to={to}
                                    data-testid={`footer-service-${to.replace(/\//g, "")}-${label.toLowerCase().split(" ")[0]}`}
                                    className="text-white/70 transition hover:text-gold-300"
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <p className="label-eyebrow mb-5">{t("footer_legal")}</p>
                    <ul className="space-y-3 text-sm">
                        {[
                            ["/terms", "Terms & Conditions"],
                            ["/privacy", "Privacy Policy"],
                            ["/refunds", "Refund & Cancellation"],
                            ["/shipping", "Shipping & Delivery"],
                        ].map(([to, label]) => (
                            <li key={to}>
                                <Link
                                    to={to}
                                    data-testid={`footer-legal-${to.replace("/", "")}`}
                                    className="text-white/70 transition hover:text-gold-300"
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <p className="label-eyebrow mb-5">{t("footer_contact")}</p>
                    <ul className="space-y-3 text-sm text-white/70">
                        <li data-testid="footer-email" className="flex items-center gap-3">
                            <Mail size={14} className="text-gold-300" />
                            {CONTACT_EMAIL}
                        </li>
                        <li data-testid="footer-phone" className="flex items-center gap-3">
                            <Phone size={14} className="text-gold-300" />
                            +91 99866 16397
                        </li>
                    </ul>
                    <div className="mt-6 flex items-center gap-3">
                        <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" data-testid="footer-linkedin" className="glass glass-hover flex h-10 w-10 items-center justify-center rounded-full">
                            <Linkedin size={15} className="text-white" />
                        </a>
                        <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="glass glass-hover flex h-10 w-10 items-center justify-center rounded-full">
                            <Instagram size={15} className="text-white" />
                        </a>
                        <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="glass glass-hover flex h-10 w-10 items-center justify-center rounded-full">
                            <MessageCircle size={15} className="text-white" />
                        </a>
                    </div>
                </div>
            </div>
            {/* SEO popular searches cluster */}
            <div className="border-t border-white/5">
                <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                    <p className="label-eyebrow mb-5">Popular Searches</p>
                    <nav data-testid="footer-seo-links" className="flex flex-wrap gap-x-5 gap-y-2.5">
                        {seoLinks.map((l) => (
                            <Link
                                key={l.label}
                                to={l.to}
                                className="text-xs text-white/40 transition hover:text-gold-300"
                            >
                                {l.label}
                            </Link>
                        ))}
                    </nav>
                </div>
            </div>
            <div className="border-t border-white/5">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-6 py-6 text-xs text-white/40 md:flex-row lg:px-10">
                    <p>© {new Date().getFullYear()} Abhijit JK. All rights reserved.</p>
                    <p>Crafted for founders who execute.</p>
                </div>
            </div>
        </footer>
    );
}
